import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import Button from '../common/Button';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import OptimizedImage from '../components/OptimizedImage';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiTarget, FiUsers, FiTrendingUp, FiAward, FiChevronDown, FiBook, FiCheckCircle } = FiIcons;

const ParallaxHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const scrollToMentorship = () => {
    const mentorshipSection = document.querySelector('#mentorship-section');
    mentorshipSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section ref={ref} className="relative h-screen w-full overflow-hidden flex items-center justify-center bg-slate-900 dark:bg-slate-950">
      <motion.div style={{ y: backgroundY }} className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-slate-900/60 dark:bg-slate-950/70 z-10" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent z-10" />

        <OptimizedImage
          src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=2000"
          alt="Real Estate Investment"
          imageKey="home_hero_bg"
          type="hero"
          priority={true}
          className="w-full h-full object-cover"
        />
      </motion.div>

      <motion.div style={{ y: textY, opacity }} className="relative z-20 text-center px-4 max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "circOut" }}>
          <span className="inline-block text-emerald-400 uppercase tracking-[0.3em] text-xs md:text-sm font-bold mb-6 border border-emerald-400/30 px-4 py-2 rounded-full backdrop-blur-sm">
            45+ Years of Real Estate Excellence
          </span>
          <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl font-bold text-white mb-8 leading-none tracking-tight drop-shadow-lg">
            Tom McKay
          </h1>
          <p className="font-sans text-xl md:text-2xl text-slate-100 mb-8 max-w-3xl mx-auto leading-relaxed font-light opacity-90 drop-shadow-md">
            Real-World Real Estate. No Hype. Build lasting wealth with proven mentorship from a self-made millionaire.
          </p>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-8 mb-12">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-emerald-400">45+</div>
              <div className="text-sm text-slate-300">Years Investing</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-emerald-400">$20M+</div>
              <div className="text-sm text-slate-300">Student Profits</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-emerald-400">Author</div>
              <div className="text-sm text-slate-300">"Wake Up & Smell the Real Estate"</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button to="/onboarding" variant="primary" icon={FiArrowRight} className="bg-emerald-500 hover:bg-emerald-600">
              Apply for Mentorship
            </Button>
            <button
              onClick={scrollToMentorship}
              className="px-8 py-4 rounded-lg font-bold transition-all bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white/20 hover:border-white/50 flex items-center space-x-2"
            >
              <span>View Programs</span>
              <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 10, 0], opacity: [0.5, 1, 0.5] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 text-white/70 cursor-pointer"
        onClick={scrollToMentorship}
      >
        <SafeIcon icon={FiChevronDown} className="w-8 h-8" />
      </motion.div>
    </section>
  );
};

const Home = () => {
  const mentorshipTiers = [
    {
      title: 'Bronze',
      subtitle: 'Self-paced foundation',
      description: 'Core video course and materials that walk you through Tom\'s fundamentals so you can understand how real deals are structured from start to finish.',
      features: ['Full FlipAnythingUSA core course', 'Entry-level access to community meetings', 'Ideal if you are early and refining your strategy'],
      icon: FiBook,
      path: '/mentorship#bronze'
    },
    {
      title: 'Silver',
      subtitle: 'Foundation + extra support',
      description: 'Everything in Bronze plus additional group meetings where you can ask Tom questions live and see how other investors are structuring deals.',
      features: ['Core course + extended group sessions', '1–2 mentorship meetings per month', 'Perfect if you want guidance while closing early deals'],
      icon: FiUsers,
      path: '/mentorship#silver'
    },
    {
      title: 'Gold',
      subtitle: 'Action-focused group mentorship',
      description: 'Gold includes everything from the lower tiers plus direct access to Tom during regular live calls and support while you work through your own active deals.',
      features: ['Live group Zoom mentorship with Tom', 'Access to a large archive of past mentorship calls', 'Priority support for time-sensitive deal questions'],
      icon: FiTrendingUp,
      popular: true,
      path: '/mentorship#gold'
    },
    {
      title: 'Platinum',
      subtitle: 'High-touch mentorship for serious investors',
      description: 'The most comprehensive access Tom offers, with deeper call archives, smaller group sessions, and more direct interaction for complex or larger deals.',
      features: ['All Gold benefits plus expanded archives', 'Smaller, higher-level mentorship sessions', 'Designed for scaling portfolios and larger projects'],
      icon: FiAward,
      path: '/mentorship#platinum'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
      <ParallaxHero />

      {/* Why Work With Tom Section */}
      <section className="py-24 px-4 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <span className="text-emerald-500 font-bold tracking-widest uppercase text-sm mb-4 block">
                  Why Investors Work with Tom
                </span>
                <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6 leading-tight">
                  This isn't a theory-based course. It's mentorship from an <span className="text-emerald-500">active investor</span>.
                </h2>
                <div className="space-y-6 text-slate-600 dark:text-slate-300">
                  <div className="flex items-start space-x-4">
                    <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-emerald-500 mt-1 flex-shrink-0" />
                    <p>Learn how to spot underpriced residential, commercial, and land deals in your own market.</p>
                  </div>
                  <div className="flex items-start space-x-4">
                    <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-emerald-500 mt-1 flex-shrink-0" />
                    <p>Get support on real deals you are working on — not just generic "one size fits all" advice.</p>
                  </div>
                  <div className="flex items-start space-x-4">
                    <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-emerald-500 mt-1 flex-shrink-0" />
                    <p>Progress through multiple mentorship tiers as your capital, confidence, and portfolio grow.</p>
                  </div>
                </div>
                <div className="mt-8">
                  <Button to="/book" variant="outline" icon={FiArrowRight}>
                    Start with Tom's Book
                  </Button>
                </div>
              </motion.div>
            </div>
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="relative rounded-2xl overflow-hidden shadow-2xl"
              >
                <img
                  src="https://flipanythingusa.com/wp-content/uploads/2025/11/Tom_Mckay_2-2.jpg"
                  alt="Tom McKay - Real Estate Investor & Mentor"
                  className="w-full h-auto"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/90 to-transparent p-8">
                  <p className="text-white font-serif text-xl">Self-made millionaire by 27</p>
                  <p className="text-slate-300">45+ years of hands-on investing experience</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Mentorship Tiers Section */}
      <section id="mentorship-section" className="py-32 px-4 bg-slate-50 dark:bg-slate-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto">
          <div className="mb-24 text-center">
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="font-serif text-5xl md:text-6xl font-bold text-slate-900 dark:text-white mb-6">
              Mentorship Paths
            </motion.h2>
            <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto mb-8">
              Whether you're making your first offer or repositioning a portfolio, there's a mentorship level designed for where you are now — and where you want to go next.
            </p>
            <div className="w-24 h-1 bg-emerald-500 mx-auto rounded-full" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mentorshipTiers.map((tier, index) => (
              <Link
                key={tier.title}
                to={tier.path}
                className="block"
              >
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`group relative h-full rounded-2xl overflow-hidden shadow-xl cursor-pointer bg-white dark:bg-slate-900 p-8 border-2 transition-all duration-300 hover:-translate-y-2 ${tier.popular ? 'border-emerald-500' : 'border-transparent hover:border-emerald-500/50'}`}
                >
                  {tier.popular && (
                    <div className="absolute top-4 right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      Most Popular
                    </div>
                  )}
                  <div className="p-4 bg-emerald-500/10 rounded-xl w-fit mb-6">
                    <SafeIcon icon={tier.icon} className="w-8 h-8 text-emerald-500" />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-slate-900 dark:text-white mb-2">{tier.title}</h3>
                  <p className="text-emerald-500 font-medium mb-4">{tier.subtitle}</p>
                  <p className="text-slate-600 dark:text-slate-400 mb-6 text-sm">{tier.description}</p>
                  <ul className="space-y-3">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-start space-x-2 text-sm text-slate-600 dark:text-slate-400">
                        <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex items-center space-x-2 text-emerald-500 font-bold group-hover:translate-x-2 transition-transform">
                    <span>Learn More</span>
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TestimonialsCarousel />

      {/* Book & Webinar Section */}
      <section className="py-24 bg-slate-900 dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-emerald-900/50 to-slate-900 p-8 md:p-16 shadow-2xl border border-emerald-500/20">
            <div className="absolute top-0 right-0 -mt-20 -mr-20 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-64 h-64 bg-emerald-600/20 rounded-full blur-3xl" />

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <span className="text-emerald-400 font-bold tracking-widest uppercase text-sm mb-4 block">
                    Get Started Today
                  </span>
                  <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                    Wake Up and Smell <br />
                    <span className="text-emerald-400">the Real Estate</span>
                  </h2>
                  <p className="text-lg text-slate-300 mb-8 max-w-lg leading-relaxed">
                    Tom's best-selling book is the foundation for his entire investing philosophy. It gives you the "why" behind the "how" and will change the way you look at money and property.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button to="/book" variant="white" icon={FiArrowRight}>
                      Get the Book
                    </Button>
                    <Button to="/onboarding" variant="outline" icon={FiArrowRight} className="border-white/30 text-white hover:bg-white/10">
                      Apply for Mentorship
                    </Button>
                  </div>
                </motion.div>
              </div>

              <div className="flex justify-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <img
                    src="https://flipanythingusa.com/wp-content/uploads/2025/11/Wake-Up-Smell-The-Real-Estate-Paperback.png"
                    alt="Wake Up and Smell the Real Estate Book Cover"
                    className="w-64 md:w-80 rounded-lg shadow-2xl transform hover:rotate-2 transition-transform duration-300"
                  />
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-40 overflow-hidden text-center bg-slate-900 dark:bg-slate-950">
        <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=2000" alt="Real Estate" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-slate-900/80 dark:bg-slate-950/90" />
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <h2 className="font-serif text-5xl md:text-7xl font-bold text-white mb-8">Ready to Build Your Empire?</h2>
          <p className="text-2xl text-slate-300 mb-12 font-light max-w-2xl mx-auto">
            If you're serious about building real estate wealth with a mentor who has done it for decades, take the next step.
          </p>
          <Button to="/onboarding" variant="white" icon={FiArrowRight} className="text-lg px-10 py-5">
            Apply for Mentorship with Tom
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;